import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type Row,
  type SortingState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import * as React from 'react';
import { cn } from '../cn';
import { Button, EmptyState, ErrorState, Input, Select, Skeleton } from './primitives';

export type { ColumnDef, SortingState, ColumnFiltersState, Row } from '@tanstack/react-table';

// ---------------------------------------------------------------------------
// Plain table primitives
// ---------------------------------------------------------------------------

export function Table({ className, ...props }: React.TableHTMLAttributes<HTMLTableElement>) {
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn('w-full caption-bottom text-sm', className)} {...props} />
    </div>
  );
}
export function TableHeader(props: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <thead {...props} className={cn('[&_tr]:border-b', props.className)} />;
}
export function TableBody(props: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody {...props} className={cn('[&_tr:last-child]:border-0', props.className)} />;
}
export function TableRow(props: React.HTMLAttributes<HTMLTableRowElement>) {
  return <tr {...props} className={cn('border-b border-border/70 transition-colors hover:bg-foreground/[0.025] data-[state=selected]:bg-accent/60', props.className)} />;
}
export function TableHead(props: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return <th {...props} className={cn('h-10 whitespace-nowrap px-4 text-left align-middle text-[12px] font-medium text-muted-foreground', props.className)} />;
}
export function TableCell(props: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return <td {...props} className={cn('px-4 py-3 align-middle', props.className)} />;
}

// ---------------------------------------------------------------------------
// DataTable: sorting, global search, per-column select filters, pagination
// ---------------------------------------------------------------------------

export type DataTableFilter = {
  /** Column id (accessorKey) to filter on. */
  columnId: string;
  label: string;
  options: { value: string; label: string }[];
};

export type DataTableProps<T> = {
  columns: ColumnDef<T, unknown>[];
  data: T[] | undefined;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  /** Placeholder for the search box; omit `searchable` to hide it. */
  searchable?: boolean;
  searchPlaceholder?: string;
  filters?: DataTableFilter[];
  /** Initial sort, e.g. [{ id: 'createdAt', desc: true }]. */
  initialSorting?: SortingState;
  /** Controlled sorting (server-side sort); pair with `manualSorting`. */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  manualSorting?: boolean;
  pageSize?: number;
  /** Disable client pagination (e.g. server cursor pagination with "Load more"). */
  paginate?: boolean;
  onRowClick?: (row: T) => void;
  getRowId?: (row: T, index: number) => string;
  empty?: { title: string; description?: React.ReactNode; action?: React.ReactNode; icon?: React.ReactNode };
  toolbar?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
};

export function DataTable<T>({
  columns,
  data,
  loading,
  error,
  onRetry,
  searchable = true,
  searchPlaceholder = 'Search…',
  filters,
  initialSorting,
  sorting: controlledSorting,
  onSortingChange,
  manualSorting,
  pageSize = 25,
  paginate = true,
  onRowClick,
  getRowId,
  empty,
  toolbar,
  footer,
  className,
}: DataTableProps<T>) {
  const [internalSorting, setInternalSorting] = React.useState<SortingState>(initialSorting ?? []);
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const rows = React.useMemo(() => data ?? [], [data]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting: controlledSorting ?? internalSorting, globalFilter, columnFilters },
    onSortingChange: onSortingChange ?? setInternalSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    manualSorting,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(paginate ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    initialState: { pagination: { pageSize } },
    globalFilterFn: 'includesString',
  });

  const visibleRows = table.getRowModel().rows;
  const hasToolbar = searchable || (filters && filters.length > 0) || toolbar;

  return (
    <div className={cn('space-y-3', className)}>
      {hasToolbar ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {searchable ? (
            <div className="relative sm:max-w-xs sm:flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label="Search"
                className="pl-8"
              />
            </div>
          ) : null}
          {filters?.map((f) => (
            <div key={f.columnId} className="sm:w-44">
              <Select
                aria-label={f.label}
                value={(table.getColumn(f.columnId)?.getFilterValue() as string | undefined) ?? ''}
                onChange={(e) => table.getColumn(f.columnId)?.setFilterValue(e.target.value || undefined)}
                options={[{ value: '', label: `All ${f.label.toLowerCase()}` }, ...f.options]}
              />
            </div>
          ))}
          {toolbar ? <div className="flex items-center gap-2 sm:ml-auto">{toolbar}</div> : null}
        </div>
      ) : null}

      {error ? (
        <ErrorState error={error} onRetry={onRetry} />
      ) : (
        <div className="overflow-hidden rounded-xl bg-card shadow-card">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id} className="hover:bg-transparent">
                  {hg.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const sorted = header.column.getIsSorted();
                    return (
                      <TableHead key={header.id} style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }} aria-sort={sorted ? (sorted === 'asc' ? 'ascending' : 'descending') : undefined}>
                        {header.isPlaceholder ? null : canSort ? (
                          <button type="button" className="inline-flex items-center gap-1 hover:text-foreground" onClick={header.column.getToggleSortingHandler()}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {sorted === 'asc' ? <ArrowUp className="size-3" /> : sorted === 'desc' ? <ArrowDown className="size-3" /> : <ArrowUpDown className="size-3 opacity-40" />}
                          </button>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {loading && rows.length === 0 ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={`s${i}`}>
                    {columns.map((_c, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full max-w-40" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : visibleRows.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={columns.length} className="p-4">
                    {rows.length === 0 && empty ? (
                      <EmptyState {...empty} className="border-0" />
                    ) : (
                      <p className="py-6 text-center text-sm text-muted-foreground">No matching results.</p>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((row: Row<T>) => (
                  <TableRow
                    key={row.id}
                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                    className={cn(onRowClick && 'cursor-pointer')}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {paginate && !error && table.getPageCount() > 1 ? (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()} · {table.getFilteredRowModel().rows.length} rows
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} aria-label="Previous page">
              <ChevronLeft />
            </Button>
            <Button variant="outline" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} aria-label="Next page">
              <ChevronRight />
            </Button>
          </div>
        </div>
      ) : null}
      {footer}
    </div>
  );
}
